import { useState, useEffect, useCallback, useRef } from "react";
import type { DebugLogEntry } from "@/types";

const MAX_LOG_ENTRIES = 500;

interface UseDebugLogOptions {
  visible?: boolean;
}

function entryKey(entry: DebugLogEntry): string {
  return entry.id || `${entry.timestamp}:${entry.level}:${entry.source}:${entry.message}`;
}

function clampEntries(entries: DebugLogEntry[]): DebugLogEntry[] {
  return entries.slice(-MAX_LOG_ENTRIES);
}

function mergeEntries(
  current: DebugLogEntry[],
  incoming: DebugLogEntry[],
): DebugLogEntry[] {
  if (incoming.length === 0) {
    return current;
  }

  const merged = [...incoming, ...current];
  const seen = new Set<string>();

  return clampEntries(
    merged.filter((entry) => {
      const key = entryKey(entry);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => a.timestamp - b.timestamp),
  );
}

export function useDebugLog({ visible = true }: UseDebugLogOptions = {}) {
  const [entries, setEntries] = useState<DebugLogEntry[]>([]);
  const entriesRef = useRef<DebugLogEntry[]>([]);
  const visibleRef = useRef(visible);

  useEffect(() => {
    visibleRef.current = visible;
    if (visible) {
      setEntries(entriesRef.current);
    }
  }, [visible]);

  const pushEntry = useCallback((entry: DebugLogEntry) => {
    entriesRef.current = clampEntries([...entriesRef.current, entry]);
    if (visibleRef.current) {
      setEntries(entriesRef.current);
    }
  }, []);

  useEffect(() => {
    if (!window.freshvibe?.onDebugLog) {
      return;
    }

    const unsub = window.freshvibe.onDebugLog((raw) => {
      pushEntry(raw as DebugLogEntry);
    });
    return () => unsub?.();
  }, [pushEntry]);

  const clear = useCallback(() => {
    entriesRef.current = [];
    if (visibleRef.current) {
      setEntries([]);
    }
  }, []);

  /** Save current entries to electron-store for a session */
  const saveEntries = useCallback((sessionId: string) => {
    if (!window.freshvibe) {
      return;
    }

    if (typeof window.freshvibe.saveSessionDebugLog === "function") {
      window.freshvibe.saveSessionDebugLog(sessionId, entriesRef.current);
      return;
    }

    window.freshvibe.saveSettings(`debugLog:${sessionId}`, entriesRef.current);
  }, []);

  /** Load persisted entries for a session */
  const loadEntries = useCallback(
    (saved: DebugLogEntry[], options?: { merge?: boolean }) => {
      entriesRef.current = options?.merge
        ? mergeEntries(entriesRef.current, saved)
        : clampEntries(saved);
      if (visibleRef.current) {
        setEntries(entriesRef.current);
      }
    },
    [],
  );

  const hasEntries = useCallback(() => entriesRef.current.length > 0, []);
  const getEntryCount = useCallback(() => entriesRef.current.length, []);

  return {
    entries,
    clear,
    pushEntry,
    saveEntries,
    loadEntries,
    hasEntries,
    getEntryCount,
  };
}
